import { useState, useCallback } from "react";
import type {
    TApiColumnMappingFieldType,
    TApiColumnMappingFileType,
    TApiSegment2MappingBehavior,
    TApiSegment4MappingBehavior,
    TApiSegment5MappingBehavior,
    TApiSegment6MappingBehavior,
    TApiSegment7MappingBehavior,
} from "@/apis/proda_finance";
import type { TFilteringRule } from "@/ui/organisms/OrganismFilteringRules";
import type {
    TColumnMappingData,
    TPropcodeMappingData,
    TSegmentMappingData,
    TSegmentNumber,
} from "./constants";
import { emptyColumnMappingData } from "./constants";

export type TOTBUploadStatus = "idle" | "uploading" | "success" | "error";

export type TOTBUploadData = {
    file: File | null;
    status: TOTBUploadStatus;
    error: string | null;
    warnings: string[];
    uploadedFileId: string | null;
    totalLineItems: number;
    totalAmount: string;
    otbPeriod: string | null;
};

export const initialOTBUploadData: TOTBUploadData = {
    file: null,
    status: "idle",
    error: null,
    warnings: [],
    uploadedFileId: null,
    totalLineItems: 0,
    totalAmount: "0",
    otbPeriod: null,
};

const initialSegmentMappings: Record<TSegmentNumber, TSegmentMappingData> = {
    2: { file: null, rows: [], error: null, warnings: [], isLoading: false },
    4: { file: null, rows: [], error: null, warnings: [], isLoading: false },
    5: { file: null, rows: [], error: null, warnings: [], isLoading: false },
    6: { file: null, rows: [], error: null, warnings: [], isLoading: false },
    7: { file: null, rows: [], error: null, warnings: [], isLoading: false },
};

const initialPropcodeMappings: TPropcodeMappingData = {
    file: null,
    rows: [],
    error: null,
    warnings: [],
    isLoading: false,
};

export type TSegmentMappingTypes = {
    segment_2_mapping_type: TApiSegment2MappingBehavior;
    segment_4_mapping_type: TApiSegment4MappingBehavior;
    segment_5_mapping_type: TApiSegment5MappingBehavior;
    segment_6_mapping_type: TApiSegment6MappingBehavior;
    segment_7_mapping_type: TApiSegment7MappingBehavior;
};

/** Optional starting values, e.g. when the wizard is prefilled from a copied deal. */
export type TDealWizardInitialState = {
    tbColumnMapping?: TColumnMappingData;
    glColumnMapping?: TColumnMappingData;
    tbFilteringRules?: TFilteringRule[];
    glFilteringRules?: TFilteringRule[];
};

/**
 * Encapsulates wizard state shared by ViewDealCreate, ViewDeals' modal,
 * and ViewServiceProviders' embedded wizard:
 * - step navigation
 * - active segment tab
 * - column mapping state for TB / GL (file, columns, mappings, filtering rules, formula modal)
 * - segment mapping state (per segment number)
 * - propcode mapping state
 * - OTB upload state
 *
 * Form state (deal name, account managers, etc.) is intentionally NOT included here;
 * those have view-specific URL-bound effects and stay in the parent view.
 */
export function useDealWizardState(initial?: TDealWizardInitialState) {
    const [currentStep, setCurrentStep] = useState<1 | 2 | 3 | 4 | 5>(1);
    const [activeSegmentTab, setActiveSegmentTab] = useState<TSegmentNumber>(2);

    const [segmentMappings, setSegmentMappings] =
        useState<Record<TSegmentNumber, TSegmentMappingData>>(initialSegmentMappings);

    const [tbColumnMapping, setTbColumnMapping] = useState<TColumnMappingData>(
        () => initial?.tbColumnMapping ?? emptyColumnMappingData,
    );
    const [glColumnMapping, setGlColumnMapping] = useState<TColumnMappingData>(
        () => initial?.glColumnMapping ?? emptyColumnMappingData,
    );
    const [tbFilteringRules, setTbFilteringRules] = useState<TFilteringRule[]>(
        () => initial?.tbFilteringRules ?? [],
    );
    const [glFilteringRules, setGlFilteringRules] = useState<TFilteringRule[]>(
        () => initial?.glFilteringRules ?? [],
    );
    const [tbFormulaOpen, setTbFormulaOpen] = useState(false);
    const [glFormulaOpen, setGlFormulaOpen] = useState(false);

    const [propcodeMappings, setPropcodeMappings] =
        useState<TPropcodeMappingData>(initialPropcodeMappings);

    const [otbUpload, setOtbUpload] = useState<TOTBUploadData>(initialOTBUploadData);
    const [usePlaceholderOTB, setUsePlaceholderOTB] = useState(false);
    const [isDraggingOTB, setIsDraggingOTB] = useState(false);

    const setColumnMapping = useCallback(
        (
            fileType: TApiColumnMappingFileType,
            updater: (prev: TColumnMappingData) => TColumnMappingData,
        ) => {
            const setter = fileType === "gl" ? setGlColumnMapping : setTbColumnMapping;
            setter(updater);
        },
        [],
    );

    const clearColumnFile = useCallback((fileType: TApiColumnMappingFileType) => {
        const setter = fileType === "gl" ? setGlColumnMapping : setTbColumnMapping;
        setter({
            file: null,
            headerRowNumber: 1,
            columns: [],
            mappings: {} as Record<TApiColumnMappingFieldType, string>,
            error: null,
            warnings: [],
            isLoading: false,
        });
    }, []);

    const clearSegmentFile = useCallback((segment: TSegmentNumber) => {
        setSegmentMappings((prev) => ({
            ...prev,
            [segment]: {
                file: null,
                rows: [],
                error: null,
                warnings: [],
                isLoading: false,
            },
        }));
    }, []);

    const clearPropcodeFile = useCallback(() => {
        setPropcodeMappings(initialPropcodeMappings);
    }, []);

    const resetWizardState = useCallback(() => {
        setCurrentStep(1);
        setActiveSegmentTab(2);
        setSegmentMappings(initialSegmentMappings);
        setTbColumnMapping(emptyColumnMappingData);
        setGlColumnMapping(emptyColumnMappingData);
        setTbFilteringRules([]);
        setGlFilteringRules([]);
        setTbFormulaOpen(false);
        setGlFormulaOpen(false);
        setPropcodeMappings(initialPropcodeMappings);
        setOtbUpload(initialOTBUploadData);
        setUsePlaceholderOTB(false);
        setIsDraggingOTB(false);
    }, []);

    return {
        currentStep,
        setCurrentStep,
        activeSegmentTab,
        setActiveSegmentTab,
        segmentMappings,
        setSegmentMappings,
        tbColumnMapping,
        setTbColumnMapping,
        glColumnMapping,
        setGlColumnMapping,
        tbFilteringRules,
        setTbFilteringRules,
        glFilteringRules,
        setGlFilteringRules,
        tbFormulaOpen,
        setTbFormulaOpen,
        glFormulaOpen,
        setGlFormulaOpen,
        propcodeMappings,
        setPropcodeMappings,
        otbUpload,
        setOtbUpload,
        usePlaceholderOTB,
        setUsePlaceholderOTB,
        isDraggingOTB,
        setIsDraggingOTB,
        setColumnMapping,
        clearColumnFile,
        clearSegmentFile,
        clearPropcodeFile,
        resetWizardState,
    } as const;
}

export type TDealWizardState = ReturnType<typeof useDealWizardState>;
